import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Table, Button, Badge, Spinner, Alert,
  Modal, OverlayTrigger, Tooltip,
} from 'react-bootstrap';
import { FiPlus, FiRotateCcw, FiEdit2, FiCopy, FiLock, FiTrash2, FiX, FiEye, FiCheckSquare } from '../../utils/icons';
import api from '../../services/api';
import { useInterfaceStore } from '../../stores/useInterfaceStore';
import { notify } from '../../stores/useNotificationStore';
import CloneQuestionnaireModal, { CloneOptions } from '../../components/CloneQuestionnaireModal';

interface QItem {
  id: number;
  nome: string;
  descricao?: string;
  framework?: string;
  is_modelo: boolean;
  is_fechado?: boolean;
  is_inativo?: boolean;
  empresa_id: number;
  total_perguntas?: number;
  total_respondidas?: number;
}

const getAutoStatus = (q: QItem): { label: string; bg: string } => {
  if (q.is_inativo) return { label: 'Inativo', bg: 'secondary' };
  if (q.is_fechado) return { label: 'Concluído', bg: 'success' };
  if (!q.total_respondidas || q.total_respondidas === 0) return { label: 'Novo', bg: 'primary' };
  return { label: `${q.total_respondidas} de ${q.total_perguntas || '?'}`, bg: 'warning' };
};

const QuestionnaireListPage: React.FC = () => {
  const [items, setItems] = useState<QItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cloneTarget, setCloneTarget] = useState<QItem | null>(null);
  const [cloning, setCloning] = useState(false);
  const [closeTarget, setCloseTarget] = useState<QItem | null>(null);
  const [closing, setClosing] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<QItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteBlockedMsg, setDeleteBlockedMsg] = useState<string | null>(null);
  const [podeInativar, setPodeInativar] = useState(false);
  const [inativando, setInativando] = useState(false);
  const [verificandoExclusao, setVerificandoExclusao] = useState(false);
  const navigate = useNavigate();
  const empresaId = useInterfaceStore((state) => state.empresaId);

  const load = () => {
    setLoading(true);
    api.getQuestionnaires().then(res => {
      if (res.success && Array.isArray(res.data)) setItems(res.data as QItem[]);
      else setError('Erro ao carregar questionários.');
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Checa de antemão se a exclusão está bloqueada, pra não mostrar "Tem certeza que deseja
  // excluir?" quando o clique em Excluir só ia resultar em erro (ex.: um Inativo que ainda
  // tem resposta herdada por equivalência em outro questionário).
  const abrirDeleteModal = async (q: QItem) => {
    setDeleteTarget(q);
    setDeleteBlockedMsg(null);
    setPodeInativar(false);
    setVerificandoExclusao(true);
    try {
      const res = await api.verificarExclusaoQuestionnaire(q.id);
      if (res.success && !(res.data as any)?.pode_excluir) {
        setDeleteBlockedMsg((res.data as any)?.error || 'Não foi possível excluir este questionário.');
        setPodeInativar(!!(res.data as any)?.pode_inativar);
      }
    } catch {
      // Se a checagem falhar, segue com a confirmação normal — a exclusão de fato
      // continua protegida pela mesma regra no DELETE.
    } finally {
      setVerificandoExclusao(false);
    }
  };

  // Tenta excluir; se o backend bloquear por "doou resposta por equivalência", mostra a
  // mensagem no próprio modal. Inativar só é oferecido como alternativa quando o
  // questionário ainda não está Inativo — uma vez Inativo, enquanto a equivalência existir
  // a exclusão fica bloqueada por completo (não existe mais nenhuma ação a seguir por
  // aqui, só Cancelar).
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await api.deleteQuestionnaire(deleteTarget.id);
      if (res.success) {
        setItems(prev => prev.filter(q => q.id !== deleteTarget.id));
        setDeleteTarget(null);
        notify.success('Questionário excluído.');
      } else {
        setDeleteBlockedMsg((res.data as any)?.error || 'Não foi possível excluir este questionário.');
        setPodeInativar(!!(res.data as any)?.pode_inativar);
      }
    } catch {
      notify.error('Erro ao excluir.');
    } finally {
      setDeleting(false);
    }
  };

  const handleInativar = async () => {
    if (!deleteTarget) return;
    setInativando(true);
    try {
      const res = await api.inativarQuestionnaire(deleteTarget.id);
      if (res.success) {
        setItems(prev => prev.map(q => q.id === deleteTarget.id ? { ...q, is_inativo: true } : q));
        setDeleteTarget(null);
        setDeleteBlockedMsg(null);
        setPodeInativar(false);
        notify.success('Questionário inativado.');
      } else {
        notify.error((res.data as any)?.error || 'Erro ao inativar questionário.');
      }
    } catch {
      notify.error('Erro ao inativar questionário.');
    } finally {
      setInativando(false);
    }
  };

  const handleClone = async (opcoes: CloneOptions) => {
    if (!cloneTarget) return;
    // Clonar um questionário normal cria a cópia na MESMA empresa dona do original — não
    // depende de ter uma empresa ativa selecionada (a grid mistura empresas, e esse clique
    // não exige ter passado por nenhuma tela de seleção). Só modelos (sem dono fixo) usam
    // a empresa ativa, pois a ideia ali é "implantar este modelo na minha empresa".
    const empresaAlvo = cloneTarget.is_modelo ? empresaId : (cloneTarget.empresa_id || empresaId);
    if (!empresaAlvo) {
      notify.warning('Selecione uma empresa ativa antes de clonar este modelo.');
      return;
    }
    setCloning(true);
    try {
      const res = await api.cloneQuestionnaire(cloneTarget.id, { ...opcoes, empresa_id: empresaAlvo });
      if (res.success) {
        notify.success('Clonado com sucesso!');
        setCloneTarget(null);
        load();
        navigate(`/app/questionarios/editar/${(res.data as any).id}`);
      } else notify.error('Erro ao clonar.');
    } catch { notify.error('Erro ao clonar.'); }
    finally { setCloning(false); }
  };

  const handleClose = async () => {
    if (!closeTarget) return;
    setClosing(true);
    try {
      const res = await api.closeQuestionnaire(closeTarget.id);
      if (res.success) {
        setItems(prev => prev.map(q => q.id === closeTarget.id ? { ...q, is_fechado: true } : q));
        setCloseTarget(null);
      } else {
        notify.error((res.data as any)?.error || 'Erro ao fechar questionário.');
      }
    } catch { notify.error('Erro ao fechar questionário.'); }
    finally { setClosing(false); }
  };

  if (loading) return <Container className="py-5 text-center"><Spinner animation="border" /></Container>;

  return (
    <Container fluid className="py-3 py-md-4">
      <div className="d-flex align-items-center justify-content-between mb-3 mb-md-4">
        <h1 className="mb-0 fs-4 fs-md-3">Questionários</h1>
        <Button size="sm" onClick={() => navigate('/app/questionarios/novo')}><FiPlus /> Novo</Button>
      </div>

      {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

      {items.length === 0 ? (
        <Alert variant="info">Nenhum questionário cadastrado.</Alert>
      ) : (
        <Table hover responsive bordered className="mb-0">
          <thead className="table-light">
            <tr>
              <th>Nome</th>
              {/* Oculta em mobile */}
              <th className="d-none d-md-table-cell" style={{ width: 200 }}>Descrição</th>
              <th className="d-none d-sm-table-cell" style={{ width: 160 }}>Framework</th>
              <th style={{ width: 110 }}>Status</th>
              <th style={{ width: 190 }} className="text-end">Ações</th>
            </tr>
          </thead>
          <tbody>
            {items.map(q => {
              const autoStatus = getAutoStatus(q);
              return (
                <tr key={q.id}>
                  <td>
                    <div className="d-flex align-items-start gap-1 flex-wrap">
                      <span className="fw-medium">{q.nome}</span>
                      <div className="d-flex gap-1">
                        {q.is_modelo && <Badge bg="info" pill>Modelo</Badge>}
                        {q.is_fechado && <Badge bg="success" pill>Fechado</Badge>}
                        {q.is_inativo && <Badge bg="secondary" pill>Inativo</Badge>}
                      </div>
                    </div>
                    {/* Mostra framework abaixo do nome em mobile */}
                    <div className="d-sm-none text-muted small mt-1">{q.framework || ''}</div>
                  </td>
                  <td className="d-none d-md-table-cell">
                    {q.descricao ? (
                      <OverlayTrigger placement="top" overlay={<Tooltip>{q.descricao}</Tooltip>}>
                        <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 190, cursor: 'help', fontSize: '0.875rem', color: '#555' }}>
                          {q.descricao}
                        </span>
                      </OverlayTrigger>
                    ) : <span className="text-muted">—</span>}
                  </td>
                  <td className="d-none d-sm-table-cell">{q.framework || '—'}</td>
                  <td><Badge bg={autoStatus.bg}>{autoStatus.label}</Badge></td>
                  <td className="text-end">
                    <div className="d-flex align-items-center gap-1 justify-content-end">
                      {q.is_fechado || q.is_inativo ? (
                        // Fechado/Inativo não recebem mais respostas — abre a mesma tela em modo
                        // leitura, já que todos os campos ficam desabilitados nesses estados.
                        <Button className="btn-icon-ghost" size="sm"
                          onClick={() => navigate(`/app/questionario/responder/${q.id}`)}
                          title="Ver">
                          <FiEye />
                        </Button>
                      ) : null}
                      {q.is_fechado && !q.is_inativo && (
                        // SM-004: anotações de auditoria sobre o já respondido — não altera a resposta.
                        <Button className="btn-icon-ghost" size="sm"
                          onClick={() => navigate(`/app/questionario/auditoria/${q.id}`)}
                          title="Auditoria">
                          <FiCheckSquare />
                        </Button>
                      )}
                      {!(q.is_fechado || q.is_inativo) && (
                        <Button className="btn-icon-ghost" size="sm"
                          onClick={() => navigate(`/app/questionario/responder/${q.id}`)}
                          disabled={q.is_modelo}
                          title={q.is_modelo ? 'Modelos não recebem respostas' : 'Responder'}>
                          <FiRotateCcw />
                        </Button>
                      )}
                      <Button className="btn-icon-ghost" size="sm"
                        onClick={() => navigate(`/app/questionarios/editar/${q.id}`)}
                        disabled={q.is_fechado || q.is_inativo}
                        title="Editar">
                        <FiEdit2 />
                      </Button>
                      <Button className="btn-icon-ghost" size="sm" onClick={() => setCloneTarget(q)} title="Clonar">
                        <FiCopy />
                      </Button>
                      {/* Sempre renderizado (só desabilitado) — removê-lo do DOM deslocava os
                          ícones à direita e abria um vão à esquerda do primeiro ícone. */}
                      <Button className="btn-icon-ghost" size="sm"
                        onClick={() => setCloseTarget(q)}
                        disabled={q.is_modelo || q.is_fechado || q.is_inativo}
                        title={q.is_modelo ? 'Modelos não são fechados' : q.is_fechado ? 'Já fechado' : q.is_inativo ? 'Questionário inativo' : 'Fechar'}>
                        <FiLock />
                      </Button>
                      <div className="icon-actions-sep" />
                      <Button className="btn-icon-ghost btn-icon-danger" size="sm" onClick={() => abrirDeleteModal(q)} title="Excluir">
                        <FiTrash2 />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}

      <CloneQuestionnaireModal
        show={!!cloneTarget} onHide={() => setCloneTarget(null)}
        onConfirm={handleClone} originalName={cloneTarget?.nome || ''}
        saving={cloning} empresaId={empresaId}
      />

      <Modal show={!!closeTarget} onHide={() => setCloseTarget(null)} centered>
        <Modal.Header closeButton><Modal.Title>Fechar Questionário</Modal.Title></Modal.Header>
        <Modal.Body>
          <p>
            Ao fechar <strong>{closeTarget?.nome}</strong>, ele ficará <strong>bloqueado para edição</strong> e
            não poderá ser reaberto. Apenas a clonagem estará disponível para criar uma nova versão.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setCloseTarget(null)}><FiX /> Cancelar</Button>
          <Button variant="danger" onClick={handleClose} disabled={closing}>
            {closing ? <Spinner as="span" size="sm" animation="border" /> : <><FiLock /> Confirmar Fechamento</>}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!deleteTarget} onHide={() => setDeleteTarget(null)} centered>
        <Modal.Header closeButton><Modal.Title>Excluir Questionário</Modal.Title></Modal.Header>
        <Modal.Body>
          {verificandoExclusao ? (
            <div className="text-center py-2"><Spinner animation="border" size="sm" /></div>
          ) : deleteBlockedMsg ? (
            <p className="mb-0">{deleteBlockedMsg}</p>
          ) : (
            <p className="mb-0">
              Tem certeza que deseja excluir <strong>{deleteTarget?.nome}</strong>? Esta ação não pode ser desfeita.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}><FiX /> Cancelar</Button>
          {!verificandoExclusao && (
            deleteBlockedMsg ? (
              podeInativar && (
                <Button variant="warning" onClick={handleInativar} disabled={inativando}>
                  {inativando ? <Spinner as="span" size="sm" animation="border" /> : 'Inativar'}
                </Button>
              )
            ) : (
              <Button variant="danger" onClick={handleDelete} disabled={deleting}>
                {deleting ? <Spinner as="span" size="sm" animation="border" /> : <><FiTrash2 /> Excluir</>}
              </Button>
            )
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default QuestionnaireListPage;
